import { Component, ErrorInfo, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * Route-level error boundary. Catches render-time throws in the wrapped
 * subtree and shows a generic fallback instead of unmounting the whole
 * React tree. The raw error is logged to the console only — never shown
 * to the user (per project error handling rules).
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="card" style={{ margin: '2rem auto', maxWidth: 480, textAlign: 'center' }}>
          <div className="card-title" style={{ marginBottom: '0.75rem' }}>
            Something went wrong loading this page.
          </div>
          <div className="page-subtitle" style={{ marginBottom: '1.25rem' }}>
            Please try again. If the problem persists, contact support.
          </div>
          <button type="button" className="btn btn-primary" onClick={this.handleReset}>
            Try again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Wraps children in an ErrorBoundary that automatically resets whenever
 * the route changes, by remounting the boundary keyed on the pathname.
 */
export function RouteErrorBoundary({ children }: { children: ReactNode }) {
  const location = useLocation();
  return <ErrorBoundary key={location.pathname}>{children}</ErrorBoundary>;
}
